import { Step, Section, Field } from 'components/Wizard2/WizardClasses';
import * as Classes from 'components/Wizard2/WizardClasses';
import shortid from 'shortid';
import deepCopy from 'deep-copy'
import { getSanitizedLocalStorageValue, getFieldByKey } from 'utilities'

const initialStepId = shortid.generate();
const initialSectionId = shortid.generate();
const initialFieldId = shortid.generate();

export const initialDesigner = {
  loading: true,
  activeStep: 0,
  wizard: {
    title: "New Wizard",
    description: "Description",
    lastModifiedDate: null,
    formDefinitionId: 0,
    category: "business",
    miscellaneousModuleId: null,
    entityTypeId: 54,
    entitySubTypeId: 202,
    sourceId: 0,
    webUserAccessLevelTypeId: 100,
    webUserAccessLevelAfterActivation: 100,
    meta: {},
    globalVariables: {
      boolean: {
        order: [],
        variables: {}
      },
      string: {
        order: [],
        variables: {}
      },
      number: {
        order: [],
        variables: {}
      },
      businessRule: {
        order: [],
        variables: {}
      }
    },
    wizardAttributesLogic: {nodes: {}, comments: {}},
    abbreviation: '',
    docTitleSrc: '',
    docId: '',
    formTitle: '',
    noLoginRequired: false,
    internalReviewRequired: false,
    notAvailableOnline: false,
    requireStaffEntry: false,
    showSignatures: true,
    stepOrder: [initialStepId],
    steps: {
      [initialStepId]: new Step({stepId: initialStepId, sectionOrder: [initialSectionId]})
    },
    sections: {
      [initialSectionId]: new Section({sectionId: initialSectionId, stepId: initialStepId, fieldOrder: [initialFieldId]})
    },
    fields: {
      [initialFieldId]: new Field({fieldId: initialFieldId, parent: {type: "SECTION", id: initialSectionId}})
    }
  },
  clipboard: null,
  selected: {},
  previewing: false,
  dirty: false,
  hoverId: null,
  auditLogs: [],
  auditError: null,
  serverOptionsCache: {},
}

const copyFieldAndSubFieldsByField = (state, incomingField, incomingSubfields) => {
  let field = deepCopy(incomingField)
  let newFieldId = shortid.generate()
  field.fieldId = newFieldId;
  // field.conditions = null;
  field.conditions = cacheConditionParentNames(field.conditions, state.wizard.fields)
  let subfields = {}

  if(field.hasOptionFields){
    let options = []
    field.options.forEach(option=>{
      let optionFields = []
      option.fields.forEach(fieldId => {
        let subfield = incomingSubfields ? incomingSubfields[fieldId] : deepCopy(state.wizard.fields[fieldId])
        let newId = shortid.generate()
        subfield.fieldId = newId;
        subfield.parent.id = newFieldId;
        // subfield.conditions = null;
        subfield.conditions = cacheConditionParentNames(subfield.conditions, state.wizard.fields)
        subfields[newId] = subfield;
        optionFields.push(newId)
      })
      option.fields = optionFields;
      options.push(option)
    })
    field.options = options;
  }

  if(field.type === 'repeater'){
    let template = []
    field.template.forEach(fieldId => {
      let newId = shortid.generate()
      let subfield = incomingSubfields ? incomingSubfields[fieldId] : deepCopy(state.wizard.fields[fieldId])
      subfield.fieldId = newId;
      subfield.parent.id = newFieldId;
      // subfield.conditions = null;
      subfield.conditions = cacheConditionParentNames(subfield.conditions, state.wizard.fields)
      subfields[newId] = subfield;
      template.push(newId)
    })
    field.template = template;
  }

  if(field.hasCheckFields){
    let checkedFields = []
    let uncheckedFields = []
    field.checkedFields.forEach(fieldId => {
      let newId = shortid.generate()
      let subfield = incomingSubfields ? incomingSubfields[fieldId] : deepCopy(state.wizard.fields[fieldId])
      subfield.fieldId = newId;
      subfield.parent.id = newFieldId;
      // subfield.conditions = null;
      subfield.conditions = cacheConditionParentNames(subfield.conditions, state.wizard.fields)
      subfields[newId] = subfield;
      checkedFields.push(newId)
    })
    field.uncheckedFields.forEach(fieldId => {
      let newId = shortid.generate()
      let subfield = incomingSubfields ? incomingSubfields[fieldId] : deepCopy(state.wizard.fields[fieldId])
      subfield.fieldId = newId;
      subfield.parent.id = newFieldId;
      // subfield.conditions = null;
      subfield.conditions = cacheConditionParentNames(subfield.conditions, state.wizard.fields)
      subfields[newId] = subfield;
      uncheckedFields.push(newId)
    })
    field.checkedFields = checkedFields;
    field.uncheckedFields = uncheckedFields;
  }
  return {field, subfields}
}

const cacheConditionParentNames = (conditions, fields) => (
  conditions ?
  conditions.map(c => ({...c, parentName: (fields[c.parentId] || {}).name}))
  : conditions
)

const getFieldsWithFilteredConditions = (fields = {}, allFields = {}) => (
  Object.entries(fields).reduce(
    (obj, [key, field]) => ({
      ...obj,
      [key]: { ...field, conditions: filterUnresolvableConditons(field.conditions, allFields) }
    }),
    {}
  )
)

const filterUnresolvableConditons = (conditions, fields) => {
  if(conditions){
    const filtered = conditions.map(c => {
      const {parentName, ...condition} = c;
      const parents = getFieldByKey(parentName, 'name', fields, 'array')
      if(parents.length === 1){
        const parent = parents[0]
        if(parent.type === condition.parentType){
          return {...condition, parentId: parent.fieldId}
        }else{
          return null
        }
      }else{
        return null
      }
    }).filter(x=>x !== null)
    if(filtered.length === 0){
      return null
    }else{
      return filtered
    }
  }
  return conditions
}

// const getArrayOfAllFieldIdsByStepId = (state, stepId) => {
//   let fields = []
//   state.wizard.steps[stepId].sectionOrder.forEach(sectionId=>{
//     fields = [...fields, ...state.wizard.sections[sectionId].fieldOrder]
//   })
//   return fields;
// }

const copySectionAndFieldsBySectionId = (state, sectionId) => {
  let fields = {}
  let subfields = {}

  let newSectionId = shortid.generate()
  let newSection = {...state.wizard.sections[sectionId], sectionId: newSectionId, fieldOrder: [], stepId: null}
  state.wizard.sections[sectionId].fieldOrder.forEach(fieldId=>{
    let {field: newField, subfields: newSubFields} = copyFieldAndSubFieldsByField(state, state.wizard.fields[fieldId])
    newSection.fieldOrder.push(newField.fieldId)
    fields = {...fields, [newField.fieldId]: {...newField, parent: {type: 'SECTION', id: newSectionId}}}
    subfields = {...subfields, ...newSubFields}
  })
  const section = newSection;

  return {section, fields, subfields}
}

const copySectionsFieldsAndSubFieldsByStepId = (state, stepId, options = {}) => {
  let sectionIds = state.wizard.steps[stepId].sectionOrder
  let sections = {}
  let fields = {}
  let subfields = {}
  sectionIds.forEach((sectionId) => {
    let newSectionId = shortid.generate()
    let newSection = {
      ...state.wizard.sections[sectionId],
      sectionId: newSectionId,
      fieldOrder: [],
      stepId,
    }
    state.wizard.sections[sectionId].fieldOrder.forEach((fieldId) => {
      let { field: newField, subfields: newSubFields } = options.preserveFieldIds
        ? { field: state.wizard.fields[fieldId], subfields }
        : copyFieldAndSubFieldsByField(state, state.wizard.fields[fieldId])
      newSection.fieldOrder.push(newField.fieldId)
      fields = {
        ...fields,
        [newField.fieldId]: { ...newField, parent: { type: 'SECTION', id: newSectionId } },
      }
      subfields = { ...subfields, ...newSubFields }
    })
    sections[newSectionId] = newSection
  })
  return { sections, fields, subfields }
}

const getFieldsWithoutSubFields = (field, fields) => {
  if(field.hasOptionFields){
    field.options.forEach((option)=>{
      option.fields.forEach((fieldId)=>{
        fields = deleteChildConditions(fields[fieldId], fields)
        delete fields[fieldId]
      })
    })
  }
  if(field.hasCheckFields){
    field.checkedFields.forEach((fieldId)=>{
      fields = deleteChildConditions(fields[fieldId], fields)
      delete fields[fieldId]
    })
    field.uncheckedFields.forEach((fieldId)=>{
      fields = deleteChildConditions(fields[fieldId], fields)
      delete fields[fieldId]
    })
  }
  if(field.template){
    field.template.forEach((fieldId)=>{
      fields = deleteChildConditions(fields[fieldId], fields)
      delete fields[fieldId]
    })
  }
  fields = deleteChildConditions(field, {...fields})
  return fields;
}

const recursivelyDeleteFields = (fieldId, fields, index, fieldOrder, callback) => {
  let computedFields = getFieldsWithoutSubFields(fields[fieldId], fields);
  delete computedFields[fieldId]
  if(fieldOrder[index + 1]){
    recursivelyDeleteFields(fieldOrder[index + 1], computedFields, index + 1, fieldOrder, callback)
  }else{
    callback(computedFields);
  }
}

const deleteStepByStepId = (state, stepId) => {
  let step = state.wizard.steps[stepId];
  let stepIndex = state.wizard.stepOrder.findIndex((fstepId) => (fstepId === stepId))
  let fields = {...state.wizard.fields};
  let sections = {...state.wizard.sections};
  let steps = {...state.wizard.steps};
  let sectionOrder = step.sectionOrder;

  sectionOrder.forEach((sectionId) => {
    let fieldOrder = sections[sectionId].fieldOrder;
    if(fieldOrder.length > 0){
      recursivelyDeleteFields(fieldOrder[0], fields, 0, fieldOrder, (computedFields) => {
        fields = computedFields;
      })
    }
    delete sections[sectionId]
  })
  delete steps[stepId]

  return {
    ...state, dirty: true,
    wizard: {
      ...state.wizard,
      fields,
      sections,
      steps,
      stepOrder: [
        ...state.wizard.stepOrder.slice(0, stepIndex),
        ...state.wizard.stepOrder.slice(stepIndex + 1)
      ]
    },
    selected: {},
    activeStep: null
  }
}

const deleteChildConditions = (field, fields) => {
  Object.keys(fields).forEach(fieldId => {
    const subfield = fields[fieldId]
    if(subfield.conditions){
      fields[fieldId] = {
        ...fields[fieldId],
        conditions: subfield.conditions.filter(cond => field ? cond.parentId !== field.fieldId : false)
      }
    }
  })
  return fields
}

const designer2Reducer = (state = initialDesigner, action) => {
  switch (action.type) {
    case "@@DESIGNER_RESET_STATE":
      return initialDesigner
    case "POPULATE_DESIGNER_STEPS":
      return {
        ...state,
        wizard: {
          ...state.wizard,
          stepOrder: action.stepOrder,
          steps: action.steps,
          sections: action.sections,
          fields: action.fields
        }
      }
    case "POPULATE_DESIGNER":
      return {
        ...state,
        loading: false,
        steps: action.steps,
        wizard: {
          ...state.wizard,
          title: action.title,
          formDefinitionId: action.formDefinitionId,
          description: action.description,
          lastModifiedDate: action.lastModifiedDate,
          lastModifiedName: action.lastModifiedName,
          category: action.category,
          stepOrder: action.stepOrder,
          steps: action.steps,
          sections: action.sections,
          fields: action.fields,
          sourceId: action.sourceId,
          abbreviation: action.abbreviation,
          meta: action.meta ? action.meta : {},
          mode: action.mode ? action.mode : '',
          docTitleSrc: action.docTitleSrc,
          docId: action.docId,
          formTitle: action.formTitle,
          internalReviewRequired: action.internalReviewRequired,
          noLoginRequired: action.noLoginRequired,
          notAvailableOnline: action.notAvailableOnline,
          requireStaffEntry: action.requireStaffEntry,
          showSignatures: action.showSignatures,
          onlyRenderChangedFieldsOnForm: action.onlyRenderChangedFieldsOnForm,
          feeDescription: action.feeDescription,
          webUserAccessLevelTypeId: action.webUserAccessLevelTypeId,
          webUserAccessLevelAfterActivation: action.webUserAccessLevelAfterActivation,
          draftsDisabled: action.draftsDisabled,
          backOfficeOnly: action.backOfficeOnly,
          ...(action.globalVariables ? {globalVariables : action.globalVariables} : {}),
          ...(action.wizardAttributesLogic ? {wizardAttributesLogic : action.wizardAttributesLogic} : {}),
          miscellaneousModuleId: action.miscellaneousModuleId || null,
        },
        selected: {},
        previewing: false
      }
    case "@@ds/SET_MISC_MODULE_ID":
      return { ...state, wizard: { ...state.wizard, miscellaneousModuleId: action.miscellaneousModuleId } }
    case "@@ds/SET_LOADING":
      return {...state, loading: action.loading}
    case "RESET_DESIGNER_STATE":
      return {...initialDesigner, clipboard: state.clipboard, loading: false}
    case "SET_FORM_DEFINITION_ID":
      return {...state, wizard: {...state.wizard, formDefinitionId: action.formDefinitionId}}
    case "SET_CURRENT_REVISION_ID":
      return {...state, wizard: {...state.wizard, currentRevisionId: action.currentRevisionId}}
    case "SET_WIZARD_REVISION_HISTORY":
      return {...state, wizard: {...state.wizard, revisionList: action.revisionList}}
    case "@@ds/SET_WIZARD_CATEGORY":
      return {...state, dirty: true, wizard: {...state.wizard, category: action.category}}
    case "@@ds/SET_AUDIT_LOGS":
      return {...state, auditLogs: action.auditLogs, auditError: null}
    case "@@ds/SET_AUDIT_ERROR":
      return {...state, auditError: action.auditError}
    case "TOGGLE_PREVIEW":
      return {...state, previewing: action.previewing}
    case "ADD_STEP":{
      let stepId = shortid.generate();
      return {
        ...state, dirty: true, wizard: {
          ...state.wizard,
          steps: {
            ...state.wizard.steps,
            [stepId]: new Step({stepId})
          },
          stepOrder: [
            ...state.wizard.stepOrder,
            stepId
          ]
        }
      }
    }
    case "ADD_SECTION":{
      let sectionId = shortid.generate();
      return {
        ...state, dirty: true, wizard: {
          ...state.wizard,
          sections: {
            ...state.wizard.sections,
            [sectionId]: new Section({sectionId, stepId: state.wizard.steps[state.wizard.stepOrder[state.activeStep]].stepId})
          },
          steps: {
            ...state.wizard.steps,
            [state.wizard.stepOrder[state.activeStep]]: {
              ...state.wizard.steps[state.wizard.stepOrder[state.activeStep]],
              sectionOrder: [...state.wizard.steps[state.wizard.stepOrder[state.activeStep]].sectionOrder, sectionId]
            }
          }
        }
      }
    }
    case "ADD_FIELD":{
      let fieldId = shortid.generate();
      return {
        ...state, dirty: true,
        selected: {type: 'FIELD', id: fieldId},
        wizard: {
          ...state.wizard,
          fields: {
            ...state.wizard.fields,
            [fieldId]: new Classes[action.fieldClass]({...action.properties, fieldId, parent: {type: "SECTION", id: action.sectionId}})
          },
          sections: {
            ...state.wizard.sections,
            [action.sectionId]: {
              ...state.wizard.sections[action.sectionId],
              fieldOrder: [...state.wizard.sections[action.sectionId].fieldOrder, fieldId]
            }
          }
        }
      }
    }
    case "ADD_FIELD_ABOVE":{
      let fieldId = shortid.generate()
      const fieldIndex = state.wizard.sections[action.sectionId].fieldOrder.findIndex(
        id => id === action.fieldId
      )
      return {
        ...state,
        dirty: true,
        selected: {type: 'FIELD', id: fieldId},
        wizard: {
          ...state.wizard,
          fields: {
            ...state.wizard.fields,
            [fieldId]: new Classes[action.fieldClass]({
              ...action.properties,
              fieldId,
              parent: { type: 'SECTION', id: action.sectionId }
            })
          },
          sections: {
            ...state.wizard.sections,
            [action.sectionId]: {
              ...state.wizard.sections[action.sectionId],
              fieldOrder: [
                ...state.wizard.sections[action.sectionId].fieldOrder.slice(0, fieldIndex),
                fieldId,
                ...state.wizard.sections[action.sectionId].fieldOrder.slice(fieldIndex),
              ]
            }
          }
        }
      }
    }
    case "ADD_FIELD_UNDER":{
      let fieldId = shortid.generate()
      const fieldIndex = state.wizard.sections[action.sectionId].fieldOrder.findIndex(
        id => id === action.fieldId
      )
      return {
        ...state,
        dirty: true,
        selected: {type: 'FIELD', id: fieldId},
        wizard: {
          ...state.wizard,
          fields: {
            ...state.wizard.fields,
            [fieldId]: new Classes[action.fieldClass]({
              ...action.properties,
              fieldId,
              parent: { type: 'SECTION', id: action.sectionId }
            })
          },
          sections: {
            ...state.wizard.sections,
            [action.sectionId]: {
              ...state.wizard.sections[action.sectionId],
              fieldOrder: [
                ...state.wizard.sections[action.sectionId].fieldOrder.slice(0, fieldIndex + 1),
                fieldId,
                ...state.wizard.sections[action.sectionId].fieldOrder.slice(fieldIndex + 1),
              ]
            }
          }
        }
      }
    }
    case "DELETE_FIELD":{
      let field = state.wizard.fields[action.fieldId];
      let sections = {...state.wizard.sections};
      let selected = {...state.selected};
      let {[action.fieldId]: toDelete, ...fields} = state.wizard.fields;
      fields = getFieldsWithoutSubFields(field, fields);

      if(field.parent){
        switch (field.parent.type.toUpperCase()) {
          case "SECTION":
            let section = state.wizard.sections[field.parent.id];
            let fieldOrderIndex = section.fieldOrder.indexOf(action.fieldId);
            sections = {
              ...sections,
              [field.parent.id]: {
                ...section,
                fieldOrder: [
                  ...section.fieldOrder.slice(0, fieldOrderIndex),
                  ...section.fieldOrder.slice(fieldOrderIndex + 1)
                ]
              }
            }
            selected = {};
            break;
          case "OPTION":
            let optionIndex = null;
            let fieldIndex = null;
            const selectedField = fields[state.selected.id] || {};
            const selectedFieldOptions = selectedField.options || [];
            selectedFieldOptions.forEach((option, i)=>{
              let index = option.fields.findIndex((fieldId)=>(fieldId === action.fieldId))
              if(index >= 0){
                optionIndex = i;
                fieldIndex = option.fields.indexOf(action.fieldId);
              }
            })
            fields = {
              ...fields,
              [state.selected.id]: {
                ...selectedField,
                options: [
                  ...selectedFieldOptions.slice(0, optionIndex),
                  {
                    ...selectedFieldOptions[optionIndex],
                    fields: [
                      ...((selectedFieldOptions[optionIndex] || {}).fields || []).slice(0, fieldIndex),
                      ...((selectedFieldOptions[optionIndex] || {}).fields || []).slice(fieldIndex + 1)
                    ]
                  },
                  ...selectedFieldOptions.slice(optionIndex + 1)
                ]
              }
            }
            break;
          case "CHECKBOX":
            let parentField = {...fields[state.selected.id]}
            let checkedFieldIndex = parentField.checkedFields.findIndex(id=>(id===action.fieldId))
            let uncheckedFieldIndex = parentField.uncheckedFields.findIndex(id=>(id===action.fieldId))
            let checkIndex = checkedFieldIndex >= 0 ? checkedFieldIndex : uncheckedFieldIndex;
            let checkType = checkedFieldIndex >= 0 ? "checkedFields" : "uncheckedFields"
            fields = {
              ...fields,
              [state.selected.id]: {
                ...parentField,
                [checkType]: [
                  ...parentField[checkType].slice(0, checkIndex),
                  ...parentField[checkType].slice(checkIndex + 1)
                ]
              }
            }
            break;
          case "REPEATER":{
            let parentId = state.selected.id;
            let parentField = state.wizard.fields[parentId]
            let templateIndex = parentField.template.findIndex(id=>(id === action.fieldId))
            //let templateName = state.wizard.fields[action.fieldId].name;
            //const { [parentId]: deletedValue, ...value } = fields[parentId].value;
            fields = {
              ...fields,
              [parentId]: {
                ...parentField,
                template: [
                  ...parentField.template.slice(0, templateIndex),
                  ...parentField.template.slice(templateIndex + 1)
                ]
                //value
              }
            }
            break;
          }
          default:
        }
      }
      return {
        ...state, dirty: true, wizard: {
          ...state.wizard,
          sections: sections,
          fields: fields
        },
        selected: selected
      }
    }
    case "DELETE_NESTED_OPTION": {
       let parentField = state.wizard.fields[action.parentId]
       let option = parentField.options[action.optionIndex]
       let fields = {...state.wizard.fields};
       option.fields.forEach(fieldId=>{
         delete fields[fieldId]
       })
       return {
         ...state,
         wizard: {
           ...state.wizard,
           fields: {
             ...fields,
             [action.parentId]: {
               ...parentField,
               options: [
                 ...parentField.options.slice(0, action.optionIndex),
                 ...parentField.options.slice(action.optionIndex + 1)
               ]
             }
           }
         }
       }
    }
    case "SELECT":
      return {...state, selected: {type: action.selectType, id: action.id}}
    case "SET_ACTIVE_STEP":
      return {...state, activeStep: action.stepIndex}
    case 'SET_FIELD' :
      return {
        ...state,
        dirty: true,
        wizard: {
          ...state.wizard,
          fields: {
            ...state.wizard.fields,
            [action.fieldId]: {
              ...action.payload,
            },
          },
        },
      }
    case "SET_FIELD_ATTRIBUTE":
      if (action.subName) {
      if (action.subSubName) {
        return {
          ...state,
          dirty: true,
          wizard: {
            ...state.wizard,
            fields: {
              ...state.wizard.fields,
              [action.fieldId]: {
                ...state.wizard.fields[action.fieldId],
                [action.attributeName]: {
                  ...state.wizard.fields[action.fieldId][action.attributeName],
                  [action.subName]: {
                    ...state.wizard.fields[action.fieldId][action.attributeName][action.subName],
                    [action.subSubName]: action.value
                  }
                },
                ...action.payload
              }
            }
          }
        }
      }
      return {
        ...state,
        dirty: true,
        wizard: {
          ...state.wizard,
          fields: {
            ...state.wizard.fields,
            [action.fieldId]: {
              ...state.wizard.fields[action.fieldId],
              [action.attributeName]: {
                ...state.wizard.fields[action.fieldId][action.attributeName],
                [action.subName]: action.value
              },
              ...action.payload
            }
          }
        }
      }
    } else {
      return {
        ...state,
        dirty: true,
        wizard: {
          ...state.wizard,
          fields: {
            ...state.wizard.fields,
            [action.fieldId]: {
              ...state.wizard.fields[action.fieldId],
              [action.attributeName]: action.value,
              ...action.payload
            }
          }
        }
      }
    }
    case "SET_SECTION_ATTRIBUTE":
      return {...state, dirty: true, wizard: {...state.wizard, sections: {
        ...state.wizard.sections,
        [action.sectionId]: {
          ...state.wizard.sections[action.sectionId],
          [action.attributeName]: action.value
        }}}}
    case "SET_STEP_ATTRIBUTE":
      return {...state, dirty: true, wizard: {...state.wizard, steps: {
        ...state.wizard.steps,
        [action.stepId]: {
          ...state.wizard.steps[action.stepId],
          [action.attributeName]: action.value
        }}}}
    case "SET_WIZARD_ATTRIBUTE":
      return {...state, dirty: true, wizard: {...state.wizard, [action.attributeName]: action.value}}
    case "SET_FIELD_OPTION_PROPERTY":
      return {...state, dirty: true, wizard: {...state.wizard, fields: {
        ...state.wizard.fields,
        [action.selectedFieldId]: {
          ...state.wizard.fields[action.selectedFieldId],
          options: [
            ...state.wizard.fields[action.selectedFieldId].options.slice(0, action.optionIndex),
            {
              ...state.wizard.fields[action.selectedFieldId].options[action.optionIndex],
              [action.attributeName]: action.value
            },
            ...state.wizard.fields[action.selectedFieldId].options.slice(action.optionIndex + 1)
          ]
        }}}}
    case "TOGGLE_VALIDATION_EDITOR":
      return {...state, validationEditorOpen: action.value}
    case "ADD_OPTION_SUBFIELD":{
      let fieldId = shortid.generate();
      let fieldIndex = state.wizard.fields[state.selected.id].options[action.optionIndex].fields.findIndex(
        id => id === action.fieldId
      )
      if(fieldIndex < 0) fieldIndex = 0;
      let option = {
        ...state.wizard.fields[state.selected.id].options[action.optionIndex],
        fields: [
          ...state.wizard.fields[state.selected.id].options[action.optionIndex].fields.slice(
            0,
            fieldIndex + (action.direction === 'under' ? 1 : 0)
          ),
          fieldId,
          ...state.wizard.fields[state.selected.id].options[action.optionIndex].fields.slice(
            fieldIndex + (action.direction === 'under' ? 1 : 0)
          ),
        ]
      }
      return {
        ...state, dirty: true,
        wizard: {
          ...state.wizard,
          fields: {
            ...state.wizard.fields,
            [state.selected.id]: {
              ...state.wizard.fields[state.selected.id],
              options: [
                ...state.wizard.fields[state.selected.id].options.slice(0, action.optionIndex),
                option,
                ...state.wizard.fields[state.selected.id].options.slice(action.optionIndex + 1)
              ]
            },
            [fieldId]: new Classes[action.fieldClass]({...action.properties, fieldId, parent: {type: "OPTION", id: state.selected.id}})
          }
        }
      }
    }
    case "ADD_CHECK_SUBFIELD":{
      let fieldId = shortid.generate();
      let fieldIndex = state.wizard.fields[state.selected.id][action.checkType].findIndex(
        id => id === action.fieldId
      )
      if(fieldIndex < 0) fieldIndex = 0;
      return {
        ...state, dirty: true,
        wizard: {
          ...state.wizard,
          fields: {
            ...state.wizard.fields,
            [state.selected.id]: {
              ...state.wizard.fields[state.selected.id],
              [action.checkType]: [
                ...state.wizard.fields[state.selected.id][action.checkType].slice(
                  0,
                  fieldIndex + (action.direction === 'under' ? 1 : 0)
                ),
                fieldId,
                ...state.wizard.fields[state.selected.id][action.checkType].slice(
                  fieldIndex + (action.direction === 'under' ? 1 : 0)
                ),
              ]
            },
            [fieldId]: new Classes[action.fieldClass]({...action.properties, fieldId, parent: {type: "CHECKBOX", id: state.selected.id}})
          }
        }
      }
    }
    case "ADD_REPEATER_SUBFIELD": {
      let fieldId = shortid.generate()
      let fieldIndex = state.wizard.fields[state.selected.id].template.findIndex(
        id => id === action.fieldId
      )
      if(fieldIndex < 0) fieldIndex = 0;
      return {
        ...state,
        dirty: true,
        wizard: {
          ...state.wizard,
          fields: {
            ...state.wizard.fields,
            [state.selected.id]: {
              ...state.wizard.fields[state.selected.id],
              template: [
                ...state.wizard.fields[state.selected.id].template.slice(
                  0,
                  fieldIndex + (action.direction === 'under' ? 1 : 0)
                ),
                fieldId,
                ...state.wizard.fields[state.selected.id].template.slice(
                  fieldIndex + (action.direction === 'under' ? 1 : 0)
                )
              ]
            },
            [fieldId]: new Classes[action.fieldClass]({
              ...action.properties,
              fieldId,
              parent: { type: 'REPEATER', id: state.selected.id }
            })
          }
        }
      }
    }
    case "ADD_NESTED_FIELD_OPTION": {
      return {
        ...state, dirty: true,
        wizard: {
          ...state.wizard,
          fields: {
            ...state.wizard.fields,
            [action.fieldId]: {
              ...state.wizard.fields[action.fieldId],
              options: [
                ...state.wizard.fields[action.fieldId].options,
                {value: "NEW_OPTION", label: "New Option", fields: []}
              ]
            }
          }
        }
      }
    }
    case "REORDER_FIELD": {
      const field = state.wizard.fields[action.fieldId];
      let newState = state;
      switch (field.parent.type) {
        case 'SECTION':{
          const currentFieldOrder = state.wizard.sections[field.parent.id].fieldOrder;
          const currentIndex = currentFieldOrder.findIndex(fieldId=>(fieldId === action.fieldId));
          let newFieldOrder;

          if(action.direction === "up"){
            newFieldOrder = [
              ...currentFieldOrder.slice(0, currentIndex - 1),
              action.fieldId,
              currentFieldOrder[currentIndex - 1],
              ...currentFieldOrder.slice(currentIndex + 1)
            ]
          }else{
            newFieldOrder = [
              ...currentFieldOrder.slice(0, currentIndex),
              currentFieldOrder[currentIndex + 1],
              action.fieldId,
              ...currentFieldOrder.slice(currentIndex + 2)
            ]
          }

          newState = {
            ...state, dirty: true,
            wizard: {
              ...state.wizard,
              sections: {
                ...state.wizard.sections,
                [field.parent.id]: {
                  ...state.wizard.sections[field.parent.id],
                  fieldOrder: newFieldOrder
                }
              }
            }
          }
          break;
        }
        case 'REPEATER':{
          const currentFieldOrder = state.wizard.fields[field.parent.id].template;
          const currentIndex = currentFieldOrder.findIndex(fieldId=>(fieldId === action.fieldId))
          let template;

          if(action.direction === "up"){
            template = [
              ...currentFieldOrder.slice(0, currentIndex - 1),
              action.fieldId,
              currentFieldOrder[currentIndex - 1],
              ...currentFieldOrder.slice(currentIndex + 1)
            ]
          }else{
            template = [
              ...currentFieldOrder.slice(0, currentIndex),
              currentFieldOrder[currentIndex + 1],
              action.fieldId,
              ...currentFieldOrder.slice(currentIndex + 2)
            ]
          }

          newState = {
            ...state, dirty: true,
            wizard: {
              ...state.wizard,
              fields: {
                ...state.wizard.fields,
                [field.parent.id]: {
                  ...state.wizard.fields[field.parent.id],
                  template
                }
              }
            }
          }
          break;
        }
        case 'OPTION': {
          const parentOptions = state.wizard.fields[field.parent.id].options;
          const optionIndex = parentOptions.findIndex(opt => opt.fields.includes(action.fieldId))
          const currentIndex = parentOptions[optionIndex].fields.findIndex(id => id === action.fieldId)
          let options = [...parentOptions];

          if(action.direction === "up" && currentIndex !== 0){
            options = [
              ...parentOptions.slice(0, optionIndex),
              {
                ...parentOptions[optionIndex],
                fields: [
                  ...parentOptions[optionIndex].fields.slice(0, currentIndex - 1),
                  action.fieldId,
                  parentOptions[optionIndex].fields[currentIndex - 1],
                  ...parentOptions[optionIndex].fields.slice(currentIndex + 1)
                ]
              },
              ...parentOptions.slice(optionIndex + 1)
            ]
          }
          if(action.direction === 'down' && currentIndex !== parentOptions[optionIndex].fields.length - 1){
            options = [
              ...parentOptions.slice(0, optionIndex),
              {
                ...parentOptions[optionIndex],
                fields: [
                  ...parentOptions[optionIndex].fields.slice(0, currentIndex),
                  parentOptions[optionIndex].fields[currentIndex + 1],
                  action.fieldId,
                  ...parentOptions[optionIndex].fields.slice(currentIndex + 2)
                ]
              },
              ...parentOptions.slice(optionIndex + 1)
            ]
          }

          newState = {
            ...state, dirty: true,
            wizard: {
              ...state.wizard,
              fields: {
                ...state.wizard.fields,
                [field.parent.id]: {
                  ...state.wizard.fields[field.parent.id],
                  options
                }
              }
            }
          }
          break;
        }
        case 'CHECKBOX': {
          const parent = state.wizard.fields[state.wizard.fields[action.fieldId].parent.id]
          let checkType = 'uncheckedFields'
          if(parent.checkedFields.find(f => f === action.fieldId)){
            checkType = 'checkedFields'
          }

          const index = state.wizard.fields[field.parent.id][checkType].findIndex(f => f === action.fieldId)
          const currentFieldOrder = state.wizard.fields[field.parent.id][checkType]

          if(action.direction === 'up'){
            newState = {
              ...state, dirty: true,
              wizard: {
                ...state.wizard,
                fields: {
                  ...state.wizard.fields,
                  [field.parent.id]: {
                    ...state.wizard.fields[field.parent.id],
                    [checkType]: [
                      ...currentFieldOrder.slice(0, index - 1),
                      action.fieldId,
                      currentFieldOrder[index - 1],
                      ...currentFieldOrder.slice(index + 1)
                    ]
                  }
                }
              }
            }
          }else{
            newState = {
              ...state, dirty: true,
              wizard: {
                ...state.wizard,
                fields: {
                  ...state.wizard.fields,
                  [field.parent.id]: {
                    ...state.wizard.fields[field.parent.id],
                    [checkType]: [
                      ...currentFieldOrder.slice(0, index),
                      currentFieldOrder[index + 1],
                      action.fieldId,
                      ...currentFieldOrder.slice(index + 2)
                    ]
                  }
                }
              }
            }
          }
          break;
        }
        default:
      }
      return newState;
    }
    case "DELETE_SECTION": {
      let sections = {...state.wizard.sections};
      let section = sections[action.sectionId];
      let fields = state.wizard.fields;
      let fieldOrder = section.fieldOrder;
      let stepId = state.wizard.steps[section.stepId]
        ? section.stepId
        : Object.keys(state.wizard.steps).find(stepId =>
            state.wizard.steps[stepId].sectionOrder.find(sId => section.sectionId === sId) ? true : false
          )
      let step = {...state.wizard.steps[stepId]};
      // console.log(section)
      let sectionOrderIndex = step.sectionOrder.findIndex((sectionId) => (sectionId === action.sectionId))

      let newFields;
      if(fieldOrder.length > 0){
        recursivelyDeleteFields(fieldOrder[0], fields, 0, fieldOrder, (computedFields)=>{
          newFields = computedFields;
        });
      }else{
        newFields = fields;
      }

      step.sectionOrder = [
        ...step.sectionOrder.slice(0, sectionOrderIndex),
        ...step.sectionOrder.slice(sectionOrderIndex + 1)
      ]
      delete sections[action.sectionId]

      return {
        ...state, dirty: true,
        wizard: {
          ...state.wizard,
          fields: newFields,
          sections,
          steps: {
            ...state.wizard.steps,
            [stepId]: step
          }
        },
        selected: {}
      }
    }
    case "DELETE_STEP": {
      return deleteStepByStepId(state, action.stepId)
    }
    case "REORDER_STEP":{
      let newStepOrder = [];
      let currentStepOrder = state.wizard.stepOrder;
      const currentIndex = action.stepIndex;
      let activeStep = state.activeStep;

      if(action.direction === "up"){
        newStepOrder = [
          ...currentStepOrder.slice(0, currentIndex - 1),
          action.stepId,
          currentStepOrder[currentIndex - 1],
          ...currentStepOrder.slice(currentIndex + 1)
        ]
        activeStep--
      }else{
        newStepOrder = [
          ...currentStepOrder.slice(0, currentIndex),
          currentStepOrder[currentIndex + 1],
          action.stepId,
          ...currentStepOrder.slice(currentIndex + 2)
        ]
        activeStep++
      }

      return {
        ...state, dirty: true,
        wizard: {
          ...state.wizard,
          stepOrder: newStepOrder
        },
        activeStep
      }
    }
    case "RECALCULATE_STEP_ORDER": {
        let newStepOrder = [];
        let currentStepOrder = state.wizard.stepOrder;
        const currentIndex = currentStepOrder.findIndex(id => id === action.reorderedStep);
        let activeStep = currentStepOrder[state.activeStep];

        // Remove the step
        newStepOrder = [
          ...currentStepOrder.slice(0, currentIndex),
          ...currentStepOrder.slice(currentIndex + 1)
        ]

        const displacedIndex = newStepOrder.findIndex(id => id === action.displacedStep);

        // Replace the step
        newStepOrder = [
          ...newStepOrder.slice(0, displacedIndex + (action.placeBefore ? 0 : 1)),
          action.reorderedStep,
          ...newStepOrder.slice(displacedIndex + (action.placeBefore ? 0 : 1))
        ]

        return {
            ...state, dirty: true,
            wizard: {
                ...state.wizard,
                stepOrder: newStepOrder
            },
            activeStep: newStepOrder.findIndex(id => id === activeStep)
        }
    }
    case "REORDER_SECTION": {
      let newSectionOrder = [];
      let section = state.wizard.sections[action.sectionId];
      let currentStepId = section.stepId ? section.stepId : state.wizard.stepOrder[state.activeStep]
      let currentSectionOrder = state.wizard.steps[currentStepId].sectionOrder;
      const currentIndex = action.sectionIndex;

      if(action.direction === "up"){
        newSectionOrder = [
          ...currentSectionOrder.slice(0, currentIndex - 1),
          action.sectionId,
          currentSectionOrder[currentIndex - 1],
          ...currentSectionOrder.slice(currentIndex + 1)
        ]
      }else{
        newSectionOrder = [
          ...currentSectionOrder.slice(0, currentIndex),
          currentSectionOrder[currentIndex + 1],
          action.sectionId,
          ...currentSectionOrder.slice(currentIndex + 2)
        ]
      }

      return {
        ...state, dirty: true,
        wizard: {
          ...state.wizard,
          steps: {
            ...state.wizard.steps,
            [currentStepId]: {
              ...state.wizard.steps[currentStepId],
              sectionOrder: newSectionOrder
            }
          }
        }
      }
    }
    case "COPY_FIELD": {
      localStorage.setItem('CLIPBOARD', JSON.stringify(copyFieldAndSubFieldsByField(state, state.wizard.fields[action.fieldId])))
      return state;
    }
    case "COPY_SECTION": {
      const copies = JSON.stringify(copySectionAndFieldsBySectionId(state, action.sectionId))
      localStorage.setItem('SECTION_CLIPBOARD', copies)
      return state;
    }
    case "COPY_STEP_CONTENTS": {
      const copies = copySectionsFieldsAndSubFieldsByStepId(state, action.stepId, {preserveFieldIds: action.preserveFieldIds})
      localStorage.setItem('STEP_CONTENTS_CLIPBOARD', JSON.stringify(copies))
      return state;
    }
    case "PASTE_SECTION": {
      const CLIPBOARD = getSanitizedLocalStorageValue('SECTION_CLIPBOARD', true)
      const fields = getFieldsWithFilteredConditions(CLIPBOARD.fields, state.wizard.fields)
      const subfields = getFieldsWithFilteredConditions(CLIPBOARD.subfields, state.wizard.fields)
      const step = state.wizard.steps[state.wizard.stepOrder[state.activeStep]]
      localStorage.removeItem('SECTION_CLIPBOARD')
      return {
        ...state,
        dirty: true,
        selected: {type: 'SECTION', id: CLIPBOARD.section.sectionId},
        wizard: {
          ...state.wizard,
          steps: {
            ...state.wizard.steps,
            [step.stepId]: {
              ...step,
              sectionOrder: [
                ...state.wizard.steps[step.stepId].sectionOrder,
                CLIPBOARD.section.sectionId
              ]
            }
          },
          sections: {
            ...state.wizard.sections,
            [CLIPBOARD.section.sectionId]: {
              ...CLIPBOARD.section,
              stepId: step.stepId
            }
          },
          fields: {
            ...state.wizard.fields,
            ...fields,
            ...subfields
          }
        }
      };
    }
    case "PASTE_STEP_CONTENTS": {
      let STEP_CONTENTS = getSanitizedLocalStorageValue('STEP_CONTENTS_CLIPBOARD')
      STEP_CONTENTS = STEP_CONTENTS ? JSON.parse(STEP_CONTENTS) : null;
      if(STEP_CONTENTS){
        let {sections: incomingSections, fields, subfields} = STEP_CONTENTS;
        fields = getFieldsWithFilteredConditions(fields, state.wizard.fields)
        subfields = getFieldsWithFilteredConditions(subfields, state.wizard.fields)
        let sections = {}
        Object.keys(incomingSections).forEach(sectionId => {
          sections[sectionId] = {...incomingSections[sectionId], stepId: action.stepId}
        })
        let step = {...state.wizard.steps[action.stepId]}
        let newState = deleteStepByStepId(state, action.stepId)
        return {
          ...newState,
          dirty: true,
          selected: {type: 'STEP', id: action.stepId},
          activeStep: state.wizard.stepOrder.findIndex(stepId=>(stepId === action.stepId)),
          wizard: {
            ...state.wizard,
            steps: {
              ...state.wizard.steps,
              [step.stepId]: {
                ...step,
                sectionOrder: Object.keys(sections).map(sectionId=>(sectionId))
              }
            },
            sections: {
              ...state.wizard.sections,
              ...sections
            },
            fields: {
              ...state.wizard.fields,
              ...fields,
              ...subfields
            }
          }
        }
      }else{
        return state
      }
    }
    case "PASTE_FIELD": {
      let CLIPBOARD = getSanitizedLocalStorageValue('CLIPBOARD')
      CLIPBOARD = CLIPBOARD ? JSON.parse(CLIPBOARD) : null
      if(CLIPBOARD){
        const newfields = copyFieldAndSubFieldsByField(state, CLIPBOARD.field, CLIPBOARD.subfields)
        const field = getFieldsWithFilteredConditions(
          { [newfields.field.fieldId]: newfields.field },
          state.wizard.fields
        )[newfields.field.fieldId]
        const subfields = getFieldsWithFilteredConditions(newfields.subfields, state.wizard.fields)
        return {
          ...state, dirty: true, wizard: {
            ...state.wizard,
            fields: {
              ...state.wizard.fields,
              [field.fieldId]: {...field, parent: {...field.parent, type: 'SECTION', id: action.sectionId}},
              ...subfields
            },
            sections: {
              ...state.wizard.sections,
              [action.sectionId]: {
                ...state.wizard.sections[action.sectionId],
                fieldOrder: [...state.wizard.sections[action.sectionId].fieldOrder, field.fieldId]
              }
            }
          }
        }
      }else{
        return state
      }
    }
    case 'SET_DIRTY':
      return {...state, dirty: action.dirty}
    case 'SET_HOVER_ID':
      return {...state, hoverId: action.hoverId}
    case '@@ds/SET_GLOBAL_VARIABLES':
      return {...state, wizard: {
        ...state.wizard,
        globalVariables: action.globalVariables
      }}
    case '@@ds/SET_OPTIONS_CACHE':
      return {
        ...state,
        serverOptionsCache: action.serverOptionsCache
      }
    case '@@ds/SET_OPTIONS_CACHE_BY_ID':
      return {
        ...state,
        serverOptionsCache: {
          ...state.serverOptionsCache,
          [action.id]: action.options
        }
      }
    default:
      return state;
  }
}

export default designer2Reducer;
